import React from 'react';
import { useSelector } from 'react-redux';

import { getStations } from '../../../../../services/api/stations';
import { radioPlayerPlayLive } from '../../../../../utils/radioPlayerEvents';

type ScheduleItem = {
  '@id'?: string;
  broadcaster?: string[] | string;
  broadcaster_title?: string;
  station?: string;
  frequency?: string;
  week_days?: string[] | string;
  week_day?: string;
  time?: string[] | string;
};

type StationInfo = {
  frequency?: string;
  name: string;
};

export type ScheduleGroup = {
  days: string[];
  key: string;
  stations: Array<StationInfo & { key: string; stationId?: string }>;
  times: string[];
};

export type ScheduleTimingSummary = {
  days: string[];
  times: string[];
};

const ORDERED_WEEKDAYS = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];

const SHORT_WEEKDAYS: Record<string, string> = {
  Domingo: 'Dom',
  Segunda: 'Seg',
  Terça: 'Ter',
  Quarta: 'Qua',
  Quinta: 'Qui',
  Sexta: 'Sex',
  Sábado: 'Sáb',
};

type Props = { schedule?: { items?: ScheduleItem[] } };

const WEEKDAY_LABELS: Record<string, string> = {
  '0': 'Domingo',
  '1': 'Segunda',
  '2': 'Terça',
  '3': 'Quarta',
  '4': 'Quinta',
  '5': 'Sexta',
  '6': 'Sábado',
  domingo: 'Domingo',
  dom: 'Domingo',
  segunda: 'Segunda',
  seg: 'Segunda',
  terca: 'Terça',
  terça: 'Terça',
  ter: 'Terça',
  quarta: 'Quarta',
  qua: 'Quarta',
  quinta: 'Quinta',
  qui: 'Quinta',
  sexta: 'Sexta',
  sex: 'Sexta',
  sabado: 'Sábado',
  sábado: 'Sábado',
  sab: 'Sábado',
};

function normalizeTime(time?: string) {
  if (!time) return '';
  const [hour, minute] = time.split(':');
  if (!hour) return time;
  return minute ? `${hour}:${minute}` : hour;
}

function normalizeMultipleValue(value?: string[] | string) {
  return (Array.isArray(value) ? value : [value]).filter(
    (entry): entry is string => typeof entry === 'string' && Boolean(entry),
  );
}

function resolveDays(item: ScheduleItem) {
  const raw = item.week_days || item.week_day || '';
  const days = Array.isArray(raw) ? raw : raw.split(',');

  return days
    .map((day) => day.trim())
    .filter(Boolean)
    .map((day) => WEEKDAY_LABELS[day] || day);
}

export function groupScheduleItems(
  items: ScheduleItem[] = [],
  stationInfo: Record<string, StationInfo> = {},
): ScheduleGroup[] {
  const groups = new Map<string, ScheduleGroup>();

  items.forEach((item, itemIndex) => {
    const broadcasters = normalizeMultipleValue(item.broadcaster);
    const broadcasterValues = broadcasters.length ? broadcasters : [''];
    const days = resolveDays(item);
    const times = normalizeMultipleValue(item.time)
      .map(normalizeTime)
      .filter(Boolean)
      .sort();
    const key = `${days.join('|') || `sem-dias-${itemIndex}`}::${
      times.join('|') || `sem-horario-${itemIndex}`
    }`;
    let group = groups.get(key);

    if (!group) {
      group = { days, key, stations: [], times };
      groups.set(key, group);
    }

    broadcasterValues.forEach((broadcaster) => {
      const info = stationInfo[broadcaster];
      const name =
        info?.name ||
        item.broadcaster_title ||
        item.station ||
        broadcaster ||
        'Rádio MEC';
      const stationKey = broadcaster || name;

      if (!group.stations.some((station) => station.key === stationKey)) {
        group.stations.push({
          frequency: item.frequency || info?.frequency,
          key: stationKey,
          name,
          stationId: broadcaster || undefined,
        });
      }
    });
  });

  return Array.from(groups.values());
}

export function getScheduleTimingSummaries(
  items: ScheduleItem[] = [],
): ScheduleTimingSummary[] {
  return groupScheduleItems(items).map(({ days, times }) => ({ days, times }));
}

export function formatScheduleDaysSummary(days: string[]): string {
  const uniqueDays = [...new Set(days)];
  const daySet = new Set(uniqueDays);

  if (ORDERED_WEEKDAYS.every((day) => daySet.has(day))) {
    return 'Todos os dias';
  }

  const weekdays = ORDERED_WEEKDAYS.slice(1, 6);
  if (
    uniqueDays.length === weekdays.length &&
    weekdays.every((day) => daySet.has(day))
  ) {
    return 'Seg a Sex';
  }

  if (
    uniqueDays.length === 2 &&
    daySet.has('Sábado') &&
    daySet.has('Domingo')
  ) {
    return 'Fim de semana';
  }

  const orderedDays = ORDERED_WEEKDAYS.filter((day) => daySet.has(day));
  const indexes = orderedDays.map((day) => ORDERED_WEEKDAYS.indexOf(day));
  const isConsecutive = indexes.every(
    (index, position) => position === 0 || index === indexes[position - 1] + 1,
  );

  if (orderedDays.length >= 3 && isConsecutive) {
    return `${SHORT_WEEKDAYS[orderedDays[0]]} a ${
      SHORT_WEEKDAYS[orderedDays[orderedDays.length - 1]]
    }`;
  }

  return orderedDays.map((day) => SHORT_WEEKDAYS[day] || day).join(', ');
}

export default function ProgramSchedule({ schedule }: Props) {
  const items = schedule?.items || [];
  const currentStationId = useSelector(
    (state: any) => state.player?.currentStationId ?? null,
  ) as string | null;
  const intentPlaying = useSelector(
    (state: any) => !!state.player?.intentPlaying,
  );
  const isPlaying = useSelector((state: any) => !!state.player?.isPlaying);
  const playerMode = useSelector(
    (state: any) => state.player?.mode ?? 'live',
  ) as string;
  const [stationInfo, setStationInfo] = React.useState<
    Record<string, StationInfo>
  >({});

  React.useEffect(() => {
    let isMounted = true;

    async function loadStationInfo() {
      const stations = await getStations();
      if (!isMounted) return;

      setStationInfo(
        Object.fromEntries(
          stations
            .filter((station) => station.id && station.shortName)
            .map((station) => [
              station.id,
              {
                frequency: station.frequency,
                name: station.shortName as string,
              },
            ]),
        ),
      );
    }

    loadStationInfo();
    return () => {
      isMounted = false;
    };
  }, []);

  const groups = groupScheduleItems(items, stationInfo);
  if (!groups.length) return null;

  return (
    <section
      id="quando-vai-ao-ar"
      className="program-view__schedule"
      aria-labelledby="quando-vai-ao-ar-title"
      tabIndex={-1}
    >
      <h2 id="quando-vai-ao-ar-title" className="program-view__asideTitle">
        Quando vai ao ar
      </h2>

      <div className="program-view__scheduleList">
        {groups.map((group) => (
          <article key={group.key} className="program-view__scheduleStationBox">
            <header className="program-view__scheduleTiming">
              <div className="program-view__scheduleTimingGroup">
                <span className="program-view__scheduleTimingLabel">
                  {group.times.length === 1 ? 'Horário' : 'Horários'}
                </span>
                <div className="program-view__scheduleTimes">
                  {group.times.length ? (
                    group.times.map((time) => (
                      <time
                        key={time}
                        className="program-view__scheduleTime"
                        dateTime={time}
                      >
                        {time}
                      </time>
                    ))
                  ) : (
                    <span className="program-view__scheduleTime">--:--</span>
                  )}
                </div>
              </div>

              {group.days.length ? (
                <div className="program-view__scheduleDaysGroup">
                  <span className="program-view__scheduleTimingLabel">
                    Dias
                  </span>
                  <p className="program-view__scheduleDaysText">
                    {group.days.join(', ')}
                  </p>
                </div>
              ) : null}
            </header>

            <div className="program-view__scheduleStations">
              <span className="program-view__scheduleStationsLabel">
                {group.stations.length === 1 ? 'Emissora' : 'Emissoras'}
              </span>

              {group.stations.map((station) => {
                const isSelected =
                  playerMode !== 'vod' &&
                  !!station.stationId &&
                  station.stationId === currentStationId;
                const isPlayingThis = isSelected && isPlaying;
                const isLoadingThis = isSelected && intentPlaying && !isPlaying;
                const hasActivePlayback = isPlayingThis || isLoadingThis;
                const stationContent = (
                  <>
                    <span className="program-view__scheduleStationMarker" />
                    <span className="program-view__scheduleStationContent">
                      <strong className="program-view__scheduleStation">
                        {station.name}
                      </strong>
                      {station.frequency ? (
                        <span className="program-view__scheduleFrequency">
                          {station.frequency}
                        </span>
                      ) : null}
                    </span>
                    {isPlayingThis || isLoadingThis ? (
                      <span
                        className={`program-view__schedulePlayingLabel${
                          isLoadingThis ? ' is-loading' : ''
                        }`}
                        aria-hidden="true"
                      >
                        {isLoadingThis ? 'carregando...' : 'tocando'}
                      </span>
                    ) : null}
                  </>
                );

                return station.stationId ? (
                  <button
                    key={station.key}
                    type="button"
                    className={`program-view__scheduleStationItem is-playable${
                      hasActivePlayback ? ' is-active' : ''
                    }${isPlayingThis ? ' is-playing' : ''}`}
                    aria-current={hasActivePlayback ? 'true' : undefined}
                    aria-pressed={hasActivePlayback}
                    aria-label={
                      isPlayingThis
                        ? `${station.name}, tocando agora`
                        : isLoadingThis
                          ? `${station.name}, carregando transmissão`
                          : `Ouvir ${station.name} ao vivo`
                    }
                    onClick={() =>
                      radioPlayerPlayLive(station.stationId, {
                        origin: 'ProgramSchedule',
                        analytics: {
                          contentType: 'radio',
                          label: station.name,
                          section: 'program.schedule',
                          stationId: station.stationId,
                        },
                      })
                    }
                  >
                    {stationContent}
                  </button>
                ) : (
                  <div
                    key={station.key}
                    className="program-view__scheduleStationItem"
                  >
                    {stationContent}
                  </div>
                );
              })}
            </div>
          </article>
        ))}
      </div>

      <div className="program-view__scheduleNotice" role="note">
        <span className="program-view__scheduleNoticeIcon" aria-hidden="true">
          i
        </span>
        <p>
          Os dias e horários deste programa fazem parte da grade regular e podem
          sofrer alterações em razão de eventos especiais ou ajustes de
          programação.
        </p>
      </div>
    </section>
  );
}
